/**
 * Persisted React Query cache lives in localStorage under uid-scoped keys
 * (`orderup:query-cache:<version>:<uid>`) managed by `providers/queryPersistence`.
 * This pure helper clears ALL of it regardless of version or uid.
 *
 * Kept dependency-free (no React / react-query / Firebase) so the sign-out
 * mutations can call it without pulling that module graph into their path —
 * mirrors `clearSelectionStorage` in ./selectionStorage.
 */

/** Key prefix shared with the persistence provider. */
export const QUERY_CACHE_STORAGE_PREFIX = 'orderup:query-cache';

/**
 * Removes every persisted query-cache blob from localStorage. Call on sign-out
 * so one user's cached data can never be restored into the next user's session
 * on a shared browser. No-op when localStorage is unavailable (SSR / privacy
 * mode).
 */
export function clearPersistedQueryCache(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        const doomed: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (key?.startsWith(QUERY_CACHE_STORAGE_PREFIX)) {
                doomed.push(key);
            }
        }
        doomed.forEach((key) => window.localStorage.removeItem(key));
    } catch {
        // Best-effort hygiene; losing the sweep costs a slower boot, not
        // correctness — the uid-scoped key means the next user reads their own
        // (absent) blob regardless.
    }
}
